/**
 * ONCALL-SCHED-01c — the CEAS handoff. /team/oncall/print?period=<id> is the
 * chrome-free sheet the office already sends the answering service: one
 * month per page, Sunday-first, PRIMARY over BACKUP in every day — generated
 * from the published calendar so it can never drift from what the phones
 * show. Optional contact page (cell numbers + escalation order). Print →
 * Save as PDF, or "Send to CEAS" from the On-Call pane (same HTML by email).
 */
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAllActiveTechs } from "@/features/board/api";
import { InfoTip } from "@/components/InfoTip";
import {
  useOncallPeriods, useOncallDays, useServiceTechs, techFirstName, periodLabel,
  parseISO, addDaysISO, addMonthsISO, monthStartISO, monthEndISO, fmtMonth, isoDate,
  type OncallDay, type OncallPeriod,
} from "./oncall";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** Months (first-of-month ISO) a period spans. */
export function monthsOf(startISO: string, endISO: string): string[] {
  const out: string[] = [];
  for (let m = monthStartISO(startISO); m <= endISO; m = addMonthsISO(m, 1)) out.push(m);
  return out;
}

/** Sunday-first rows of a month, null = outside the month. */
export function monthGrid(monthISO: string): (string | null)[][] {
  const first = parseISO(monthISO);
  const lead = first.getDay();
  const end = monthEndISO(monthISO);
  const cells: (string | null)[] = Array.from({ length: lead }, () => null);
  for (let d = monthISO; d <= end; d = addDaysISO(d, 1)) cells.push(d);
  while (cells.length % 7) cells.push(null);
  const rows: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
  return rows;
}

export interface ContactRow { empno: string; name: string; cell: string | null }

/** Staff cell numbers for the contact page — the Pegasus roster first, ESC's record as the fallback. */
export function useOncallContacts(empnos: string[]) {
  return useQuery({
    queryKey: ["oncall_contacts", [...empnos].sort().join(",")],
    enabled: empnos.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ContactRow[]> => {
      const [{ data: prof }, { data: emps }] = await Promise.all([
        supabase.from("tech_profile").select("empno, display_name, cellphone").in("empno", empnos),
        supabase.from("mirror_employee").select("empno, firstname, lastname, empname, cellphone").in("empno", empnos),
      ]);
      const p = new Map(((prof ?? []) as { empno: string; display_name: string | null; cellphone: string | null }[]).map((r) => [r.empno, r]));
      return ((emps ?? []) as { empno: string; firstname: string | null; lastname: string | null; empname: string | null; cellphone: string | null }[])
        .map((e) => ({
          empno: e.empno,
          name: p.get(e.empno)?.display_name ?? (`${e.firstname ?? ""} ${e.lastname ?? ""}`.trim() || e.empname || e.empno),
          cell: p.get(e.empno)?.cellphone ?? e.cellphone ?? null,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}

/** The same sheet as plain HTML with inline styles — the body of the CEAS email. */
export function renderOncallSheetHtml(opts: {
  period: OncallPeriod;
  days: OncallDay[];
  nameOf: (empno: string | null) => string;
  contacts: ContactRow[] | null;
  generatedBy: string | null;
}): string {
  const { period, days, nameOf, contacts } = opts;
  const map = new Map(days.map((d) => [d.day, d]));
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const months = monthsOf(period.starts_on, period.ends_on).map((m) => {
    const rows = monthGrid(m).map((row) => "<tr>" + row.map((iso) => {
      if (!iso) return '<td style="border:1px solid #cbd5e1;height:70px;background:#f8fafc"></td>';
      const d = map.get(iso);
      const inside = iso >= period.starts_on && iso <= period.ends_on;
      const body = d
        ? `<div style="text-align:center;margin-top:8px;font-weight:700;font-size:13px">${esc(nameOf(d.primary_empno).toUpperCase())}</div><div style="text-align:center;font-size:13px">${d.backup_empno ? esc(nameOf(d.backup_empno).toUpperCase()) : "&nbsp;"}</div>`
        : "";
      return `<td style="border:1px solid #cbd5e1;height:70px;vertical-align:top;padding:4px;${inside ? "" : "background:#f8fafc"}"><div style="font-size:11px;font-weight:700;color:#334155">${Number(iso.slice(8, 10))}</div>${body}</td>`;
    }).join("") + "</tr>").join("");
    return `<h2 style="font-family:Georgia,serif;font-size:22px;text-align:center;margin:18px 0 6px">${esc(fmtMonth(m))} — On-Call</h2>
<table style="border-collapse:collapse;width:100%;table-layout:fixed;font-family:Arial,sans-serif"><thead><tr>${WEEKDAYS.map((w) => `<th style="border:1px solid #cbd5e1;font-size:11px;padding:4px;background:#f1f5f9">${w}</th>`).join("")}</tr></thead><tbody>${rows}</tbody></table>`;
  }).join("");
  const contactHtml = contacts && contacts.length
    ? `<h3 style="font-family:Arial,sans-serif;font-size:14px;margin:18px 0 6px">Reach the on-call tech</h3><p style="font-family:Arial,sans-serif;font-size:12px;margin:0 0 6px">Call the PRIMARY first; if there is no answer, the BACKUP; then the office line.</p><table style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:12px">${contacts.map((c) => `<tr><td style="border:1px solid #cbd5e1;padding:3px 8px">${esc(c.name)}</td><td style="border:1px solid #cbd5e1;padding:3px 8px">${esc(c.cell ?? "—")}</td></tr>`).join("")}</table>`
    : "";
  return `<div style="font-family:Arial,sans-serif;color:#0f172a"><p style="font-size:13px;margin:0 0 4px"><strong>Sedgwick Heating — service on-call schedule, ${esc(period.label ?? periodLabel(period.starts_on, period.ends_on))}.</strong> Top name = primary, second name = backup.</p>${months}${contactHtml}<p style="font-size:10px;color:#64748b;margin-top:14px">Generated from Pegasus ${esc(isoDate(new Date()))}${opts.generatedBy ? ` by ${esc(opts.generatedBy)}` : ""}. If a name changes, a new sheet follows.</p></div>`;
}

export function OncallPrintView() {
  const [params] = useSearchParams();
  const periodId = params.get("period");
  const withContacts = params.get("contacts") === "1";
  const { data: periods } = useOncallPeriods();
  const period = useMemo(() => (periods ?? []).find((p) => p.id === periodId) ?? null, [periods, periodId]);
  const { data: days } = useOncallDays(period?.starts_on ?? "2000-01-01", period?.ends_on ?? "2000-01-01", !!period);
  const { techs } = useServiceTechs();
  const all = useAllActiveTechs().data ?? [];
  const nameOf = (e: string | null) => { if (!e) return ""; const t = techs.find((x) => x.empno === e) ?? all.find((x) => x.empno === e); return t ? techFirstName(t) : e; };
  const empnos = useMemo(() => Array.from(new Set((days ?? []).flatMap((d) => [d.primary_empno, d.backup_empno].filter(Boolean) as string[]))), [days]);
  const contacts = useOncallContacts(withContacts ? empnos : []).data ?? null;

  useEffect(() => { document.title = period ? `On-Call · ${period.label ?? periodLabel(period.starts_on, period.ends_on)}` : "On-Call"; }, [period]);

  if (!period || !days) {
    return <div className="p-8 text-sm text-slate-500">{periods && !period ? "That on-call period does not exist." : "Loading the on-call sheet…"}</div>;
  }
  const map = new Map(days.map((d) => [d.day, d]));
  return (
    <div className="mx-auto max-w-[8.5in] bg-white p-6 text-slate-900 print:p-0">
      <style>{`@media print { @page { size: letter portrait; margin: 0.5in; } .sheet-page { page-break-after: always; } .sheet-page:last-child { page-break-after: auto; } .no-print { display: none !important; } }`}</style>
      <div className="no-print mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-hairline bg-slate-50 px-3 py-2 text-[13px] text-slate-600">
        <span className="inline-flex items-center gap-1.5 font-semibold text-slate-700">
          On-Call Sheet for CEAS
          <InfoTip label="About this sheet">The sheet CEAS gets, one month per page. Print, then choose Save as PDF, or use Send to CEAS on Team → On-Call.</InfoTip>
        </span>
        <button type="button" onClick={() => window.print()} className="ml-auto rounded-lg bg-slate-900 px-3 py-1.5 text-[12.5px] font-semibold text-white">Print</button>
      </div>
      {monthsOf(period.starts_on, period.ends_on).map((m) => (
        <section key={m} className="sheet-page mb-8">
          <h1 className="text-center font-serif text-2xl font-bold">{fmtMonth(m)} — On-Call</h1>
          <p className="text-center text-[11px] text-slate-500">Sedgwick Heating · service techs · top name = primary, second name = backup · {period.label ?? periodLabel(period.starts_on, period.ends_on)}</p>
          <table className="mt-3 w-full table-fixed border-collapse">
            <thead><tr>{WEEKDAYS.map((w) => <th key={w} className="border border-slate-400 bg-slate-100 py-1 text-[11px] font-semibold">{w}</th>)}</tr></thead>
            <tbody>
              {monthGrid(m).map((row, ri) => (
                <tr key={ri}>
                  {row.map((iso, ci) => {
                    if (!iso) return <td key={ci} className="h-[4.6rem] border border-slate-400 bg-slate-50" />;
                    const d = map.get(iso);
                    const inside = iso >= period.starts_on && iso <= period.ends_on;
                    return (
                      <td key={iso} className={`h-[4.6rem] border border-slate-400 p-1 align-top ${inside ? "" : "bg-slate-50"}`}>
                        <div className="text-[11px] font-bold">{Number(iso.slice(8, 10))}</div>
                        {d && (
                          <div className="mt-1.5 text-center text-[13px] leading-tight">
                            <div className="font-bold uppercase">{nameOf(d.primary_empno)}</div>
                            <div className="uppercase">{d.backup_empno ? nameOf(d.backup_empno) : " "}</div>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
      {withContacts && contacts && contacts.length > 0 && (
        <section className="sheet-page">
          <h2 className="font-serif text-xl font-bold">Reach the on-call tech</h2>
          <p className="text-[12px] text-slate-600">Call the PRIMARY first; if there is no answer, the BACKUP; then the office line.</p>
          <table className="mt-2 border-collapse text-[13px]">
            <tbody>
              {contacts.map((c) => (
                <tr key={c.empno}><td className="border border-slate-400 px-3 py-1 font-semibold">{c.name}</td><td className="border border-slate-400 px-3 py-1 tabular-nums">{c.cell ?? "—"}</td></tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      <p className="mt-4 text-[10px] text-slate-400">Generated from Pegasus {isoDate(new Date())}. If a name changes, a new sheet follows.</p>
    </div>
  );
}
